import { useState } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import {
  actSubjects,
  subjectQuestionCount,
} from "../../actprep-ui/constants/program";
import { capitalize } from "../../actprep-ui/utils/formatting";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import {
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  ImageList,
  ImageListItem,
  Stack,
} from "@mui/material";
import {
  Add,
  Delete,
  Image,
  PlayCircle,
  UploadFile,
  Warning,
} from "@mui/icons-material";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import api from "../../actprep-ui/utils/api";
import VideoViewer from "../../actprep-ui/components/VideoViewer";
import { testingVariants } from "../../actprep-ui/constants/testing";
import { sexyBooleanColumnTrueOnly } from "../../actprep-ui/constants/dataGrid";
import { dataGridNestedColumnGetter } from "../../actprep-ui/utils/tables";
import TestQuestionForm from "../TestQuestionForm";
import { useConfirm } from "material-ui-confirm";
import {
  showErrorSnackbar,
  showSuccessSnackbar,
} from "../../actprep-ui/utils/feedback";
import TestAnswersImportDialog from "./TestAnswersImportDialog";

export default function TestAnswersEditor({ test }) {
  const [subject, setSubject] = useState(
    test.variant == testingVariants.LESSON ? "lesson" : "english"
  );
  const confirm = useConfirm();
  const [editRowId, setEditRowId] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const testQuestionFormDisc = useDisclosure({
    onClose: () => {
      setEditRowId(null);
      refetchTestQuestions();
    },
  });
  const viewImagesDisclosure = useDisclosure({
    onClose: () => {
      setSelectedImages([]);
    },
  });
  const viewVideoViewerDisclosure = useDisclosure({
    onClose: () => {
      setSelectedVideo(null);
    },
  });
  const importDialogDisclosure = useDisclosure();

  const { data: testQuestionData, refetch: refetchTestQuestions } = useGet(
    apiUrls.admin.testQuestions(),
    {
      params: {
        search_params: {
          subject: subject,
          test_id: test.uuid,
        },
        keep_related: true,
        sign_images: true,
        exclude: ["test"],
        order_by: ["question_number"],
      },
      staleTime: 0,
    }
  );

  const testQuestions = testQuestionData?.data || [];
  const isLessonTest = test.variant == testingVariants.LESSON;
  const lessonFirstQuestion = Number(test.lesson_first_question) || 1;
  const expectedQuestionCount = isLessonTest
    ? Number(test.lesson_question_count) || 0
    : subjectQuestionCount[test.variant][subject];
  const lessonLastQuestion = isLessonTest
    ? lessonFirstQuestion + expectedQuestionCount - 1
    : null;

  const actualQuestionCount = testQuestions.length;
  const questionCountMismatch =
    testQuestionData && actualQuestionCount !== expectedQuestionCount;
  const outOfRangeQuestions = isLessonTest
    ? testQuestions.filter(
        (question) =>
          question.question_number < lessonFirstQuestion ||
          question.question_number > lessonLastQuestion
      )
    : [];

  const columns = [
    { field: "question_number", headerName: "Question Number", width: 150 },
    {
      field: "correct_answer",
      headerName: "Answer",
      width: 150,
    },
    {
      field: "answer_choices",
      headerName: "Choices",
      valueGetter: (val) => val.sort().join(","),
      width: 150,
    },
    {
      field: "category.name",
      headerName: "Category",
      width: 200,
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "video.title",
      headerName: "Video",
      width: 200,
      renderCell: (params) => {
        const video = params.row.video;
        if (!video) {
          return <Typography variant="body2">-</Typography>;
        }
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">{video.title}</Typography>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setSelectedVideo(video);
                viewVideoViewerDisclosure.onOpen();
              }}
              title="View Video"
            >
              <PlayCircle fontSize="small" />
            </IconButton>
          </Stack>
        );
      },
    },
    {
      field: "image_count",
      headerName: "Images",
      width: 150,
      renderCell: (params) => {
        const imageCount = params.row.image_count || 0;
        if (imageCount === 0) {
          return <Typography variant="body2">0</Typography>;
        }
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">{imageCount}</Typography>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setSelectedImages(params.row.images || []);
                viewImagesDisclosure.onOpen();
              }}
              title="View Images"
            >
              <Image fontSize="small" />
            </IconButton>
          </Stack>
        );
      },
    },
    {
      field: "is_field_test_question",
      headerName: "Field Test Question?",
      width: 200,
      renderCell: sexyBooleanColumnTrueOnly,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          key="end"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => {
            setEditRowId(params.id);
            testQuestionFormDisc.onOpen();
          }}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<Delete />}
          label="Delete"
          onClick={() =>
            confirm({
              title: "Are you sure?",
              description: `Are you sure you want to delete ${
                test.name
              } ${capitalize(subject)} #${
                params.row.question_number
              }? This action is irreversible.`,
              confirmationText: "Delete",
              cancellationText: "Cancel",
              confirmationButtonProps: {
                color: "error",
                variant: "contained",
              },
            })
              .then(async () => {
                const response = await api.delete(
                  apiUrls.admin.testQuestions(params.id)
                );
                if (response.data?.success) {
                  showSuccessSnackbar("Question deleted successfully");
                  refetchTestQuestions();
                } else {
                  showErrorSnackbar(
                    response.data?.reason || "Failed to delete question"
                  );
                }
              })
              .catch(() => {})
          }
        />,
      ],
    },
  ];

  return (
    <Box>
      <Drawer
        {...testQuestionFormDisc.getDisclosureProps()}
        sx={(theme) => ({
          width: "fit-content",
          minWidth: "20rem",
          zIndex: theme.zIndex.modal + 1,
        })}
        anchor="right"
      >
        <Box
          p={2}
          sx={{
            width: "20rem",
          }}
        >
          <TestQuestionForm
            test={test}
            testQuestionId={editRowId}
            subject={subject}
            onSubmit={testQuestionFormDisc.onClose}
          />
        </Box>
      </Drawer>

      {/* View Images Dialog */}
      <Dialog
        {...viewImagesDisclosure.getDisclosureProps()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Question Images</DialogTitle>
        <DialogContent>
          <ImageList cols={1} gap={16}>
            {selectedImages.map((img, index) => (
              <ImageListItem key={img.uuid || index}>
                <img
                  src={img.image}
                  alt={`Question image ${index + 1}`}
                  loading="lazy"
                  style={{ cursor: "pointer" }}
                  onClick={() => window.open(img.image, "_blank")}
                />
                <Stack direction="row" spacing={1} mt={1}>
                  <Chip
                    label={img.image_type}
                    size="small"
                    color={
                      img.image_type === "Primary" ? "primary" : "secondary"
                    }
                  />
                  {img.has_diagram && <Chip label="Has Diagram" size="small" />}
                </Stack>
              </ImageListItem>
            ))}
          </ImageList>
        </DialogContent>
        <DialogActions>
          <Button onClick={viewImagesDisclosure.onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* View Video Viewer Dialog */}
      <Dialog
        {...viewVideoViewerDisclosure.getDisclosureProps()}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {selectedVideo && <VideoViewer video={selectedVideo} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={viewVideoViewerDisclosure.onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Import CSV Dialog */}
      <TestAnswersImportDialog
        open={importDialogDisclosure.isOpen}
        onClose={() => {
          refetchTestQuestions();
          importDialogDisclosure.onClose();
        }}
        test={test}
        subject={subject}
        expectedQuestionCount={expectedQuestionCount}
      />

      {[testingVariants.CLASSIC, testingVariants.TWENTYTWENTYFIVE].includes(
        test.variant
      ) && (
        <FormControl fullWidth margin="normal" variant="standard">
          <InputLabel>Subject</InputLabel>
          <Select
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            label="Subject"
          >
            {actSubjects.map((subject) => (
              <MenuItem key={subject} value={subject}>
                {capitalize(subject)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {questionCountMismatch && (
        <Alert
          severity="warning"
          sx={{
            my: 2,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
          icon={<Warning />}
        >
          <Typography
            variant="body1"
            component="div"
            sx={{ fontWeight: "bold", mb: 0.5 }}
          >
            QUESTION COUNT MISMATCH
          </Typography>
          <Typography variant="body2" component="div">
            Expected: <strong>{expectedQuestionCount}</strong> questions |
            Actual: <strong>{actualQuestionCount}</strong> questions
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
            {actualQuestionCount < expectedQuestionCount
              ? `Missing ${
                  expectedQuestionCount - actualQuestionCount
                } question(s). Please add the remaining questions.`
              : `Too many questions. Expected ${expectedQuestionCount} but found ${actualQuestionCount}. Please remove ${
                  actualQuestionCount - expectedQuestionCount
                } question(s).`}
          </Typography>
          {isLessonTest && expectedQuestionCount > 0 && (
            <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
              This section covers questions {lessonFirstQuestion}–
              {lessonLastQuestion}.
            </Typography>
          )}
        </Alert>
      )}

      {outOfRangeQuestions.length > 0 && (
        <Alert severity="warning" sx={{ my: 2 }} icon={<Warning />}>
          <Typography variant="body2">
            {outOfRangeQuestions.length} question
            {outOfRangeQuestions.length === 1 ? "" : "s"} fall outside questions{" "}
            {lessonFirstQuestion}–{lessonLastQuestion}. The answer sheet only
            includes questions in that slice.
          </Typography>
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ my: 2 }}>
        <Chip
          label="Import File"
          onClick={importDialogDisclosure.onOpen}
          icon={<UploadFile />}
          size="small"
        />
        <Chip
          label="Add Question"
          onClick={testQuestionFormDisc.onToggle}
          icon={<Add />}
          size="small"
        />
      </Stack>
      <Box sx={{ width: "100%", height: 500 }}>
        <DataGridPro
          rows={testQuestions}
          getRowId={({ uuid }) => uuid}
          columns={columns}
          initialState={{ pinnedColumns: { right: ["actions"] } }}
          disableColumnSorting
          disableColumnMenu
        />
      </Box>
    </Box>
  );
}
